import { useCallback, useMemo, useState } from 'react';
import type { GridSortModel } from '@mui/x-data-grid';
import type { IncidentOpsQueueFilter } from '../../domain/incidents/incidentOwnershipQueueFilter';
import type { IncidentSeverity } from '../../services/incidentsApi';
import { DEFAULT_INCIDENT_TABLE_MODE, type IncidentTableMode } from './incidentTableMode';

export type { IncidentOpsQueueFilter };
export type { IncidentTableMode };
export type OperatorPreset = '' | 'unack_high' | 'open_gt_10m' | 'repeat_zone';
export type IncidentStatusFilter = '' | 'OPEN' | 'ACKED' | 'IN_REVIEW' | 'CLOSED' | 'SUPPRESSED';
export type IncidentWorkflowStatusFilter = '' | 'Needs review' | 'Assigned' | 'Needs action' | 'Resolved' | 'Reviewed' | 'Suppressed' | 'Unmapped';

export function useIncidentsFilters(initialPreset: OperatorPreset) {
  const [tabValue, setTabValue] = useState(1);
  const [tableMode, setTableMode] = useState<IncidentTableMode>(initialPreset === 'unack_high' ? 'raw' : DEFAULT_INCIDENT_TABLE_MODE);
  const [operatorPreset, setOperatorPreset] = useState<OperatorPreset>(initialPreset);

  const [filterIncidentType, setFilterIncidentType] = useState<string>('');
  const [filterSeverity, setFilterSeverity] = useState<IncidentSeverity | ''>(initialPreset === 'unack_high' ? 'high' : '');
  const [filterZoneId, setFilterZoneId] = useState<string>('');
  const [filterDeviceId, setFilterDeviceId] = useState<string>('');
  const [filterCameraId, setFilterCameraId] = useState<string>('');
  const [filterIncidentStatus, setFilterIncidentStatus] = useState<IncidentStatusFilter>('');
  const [filterWorkflowStatus, setFilterWorkflowStatus] = useState<IncidentWorkflowStatusFilter>(initialPreset === 'unack_high' ? 'Needs review' : '');
  const [filterOpsQueue, setFilterOpsQueue] = useState<IncidentOpsQueueFilter>('');
  const [pendingQueueAutoOpen, setPendingQueueAutoOpen] = useState(false);
  const [filterOpsAssignee, setFilterOpsAssignee] = useState<string>('');
  const [filterMinAgeMinutes, setFilterMinAgeMinutes] = useState<number | ''>('');
  const [searchIncidentId, setSearchIncidentId] = useState<string>('');
  /** 0-23, UTC (matches server heatmap buckets on incident created_at) */
  const [filterHeatmapHour, setFilterHeatmapHour] = useState<number | null>(null);
  /** Pack key from Risk Analytics (`policy_pack` URL); client-side filter on loaded rows. */
  const [filterPolicyPack, setFilterPolicyPack] = useState<string>('');

  const advancedTableFilterCount = useMemo(
    () =>
      [
        operatorPreset,
        filterIncidentType,
        filterZoneId,
        filterCameraId,
        filterDeviceId,
        filterWorkflowStatus,
        filterOpsAssignee,
        filterMinAgeMinutes,
        searchIncidentId,
        filterHeatmapHour === null ? '' : '1',
        filterPolicyPack,
      ].filter((value) => String(value || '').trim()).length,
    [
      operatorPreset,
      filterIncidentType,
      filterZoneId,
      filterCameraId,
      filterDeviceId,
      filterWorkflowStatus,
      filterOpsAssignee,
      filterMinAgeMinutes,
      searchIncidentId,
      filterHeatmapHour,
      filterPolicyPack,
    ],
  );

  const _defaultSortModel = useCallback(
    (mode: IncidentTableMode, wantSeverity: boolean): GridSortModel => {
      if (mode === 'grouped') return [{ field: 'last_seen_at', sort: 'desc' }];
      if (wantSeverity) return [{ field: 'severity', sort: 'desc' }, { field: 'created_at', sort: 'desc' }];
      return [{ field: 'created_at', sort: 'desc' }];
    },
    [],
  );

  const [sortBySeverity, setSortBySeverity] = useState<boolean>(initialPreset === 'unack_high');
  const [sortModel, setSortModel] = useState<GridSortModel>(_defaultSortModel(initialPreset === 'unack_high' ? 'raw' : 'grouped', initialPreset === 'unack_high'));

  const _applyPreset = useCallback(
    (preset: OperatorPreset) => {
      setOperatorPreset(preset);
      if (preset === 'unack_high') {
        setTabValue(1);
        setTableMode('raw');
        setFilterOpsQueue('');
        setFilterWorkflowStatus('Needs review');
        setFilterSeverity('high');
        setFilterMinAgeMinutes('');
        setSortBySeverity(true);
        setSortModel(_defaultSortModel('raw', true));
        return;
      }
      if (preset === 'open_gt_10m') {
        setTabValue(1);
        setTableMode('raw');
        setFilterOpsQueue('');
        setFilterWorkflowStatus('');
        setFilterSeverity('');
        setFilterMinAgeMinutes(10);
        setSortBySeverity(false);
        setSortModel([{ field: 'created_at', sort: 'asc' }]);
        return;
      }
      if (preset === 'repeat_zone') {
        setTabValue(1);
        setTableMode('grouped');
        setFilterOpsQueue('');
        setFilterWorkflowStatus('');
        setFilterSeverity('');
        setFilterMinAgeMinutes('');
        setSortBySeverity(false);
        setSortModel(_defaultSortModel('grouped', false));
        return;
      }

      setFilterMinAgeMinutes('');
      setSortBySeverity(false);
      setSortModel(_defaultSortModel(tableMode, false));
    },
    [_defaultSortModel, tableMode],
  );

  return {
    tabValue,
    setTabValue,
    tableMode,
    setTableMode,
    operatorPreset,
    setOperatorPreset,
    filterIncidentType,
    setFilterIncidentType,
    filterSeverity,
    setFilterSeverity,
    filterZoneId,
    setFilterZoneId,
    filterDeviceId,
    setFilterDeviceId,
    filterCameraId,
    setFilterCameraId,
    filterIncidentStatus,
    setFilterIncidentStatus,
    filterWorkflowStatus,
    setFilterWorkflowStatus,
    filterOpsQueue,
    setFilterOpsQueue,
    pendingQueueAutoOpen,
    setPendingQueueAutoOpen,
    filterOpsAssignee,
    setFilterOpsAssignee,
    filterMinAgeMinutes,
    setFilterMinAgeMinutes,
    searchIncidentId,
    setSearchIncidentId,
    filterHeatmapHour,
    setFilterHeatmapHour,
    filterPolicyPack,
    setFilterPolicyPack,
    advancedTableFilterCount,
    sortBySeverity,
    setSortBySeverity,
    sortModel,
    setSortModel,
    _defaultSortModel,
    _applyPreset,
  };
}
